import { Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoModule } from '@jsverse/transloco';
import { finalize } from 'rxjs/operators';
import { AuthService, UpdateUserRequestBody, User } from '../../auth';
import { AlertModule } from 'ngx-bootstrap/alert';
import { LoaderGlobeAnimationDetailComponent, LoadingObject, LoadingService } from '../../loaders';
import { CommonFormValidationMessagesComponent } from '../../forms';

@Component({
  selector: 'multisite-profile-user-name',
  templateUrl: './profile-user-name.component.html',
  styleUrls: ['./profile-user-name.component.scss'],
  imports: [
    ReactiveFormsModule,
    TranslocoModule,
    AlertModule,
    LoaderGlobeAnimationDetailComponent,
    CommonFormValidationMessagesComponent,
  ],
})
export class ProfileUserNameComponent {
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  readonly user = signal<User | null | undefined>(undefined);
  loadingObject = signal<LoadingObject>({});
  loadingService = inject(LoadingService);
  readonly submitAttempted = signal(false);
  readonly error = signal<string | null>(null);

  readonly nameForm = new FormGroup({
    fname: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(2), Validators.maxLength(60)],
    }),
    lname: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(2), Validators.maxLength(60)],
    }),
    pre_honorific: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.maxLength(50)],
    }),
    post_honorific: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.maxLength(20)],
    }),
  });

  readonly initialLoading = computed(() => this.user() === undefined);
  readonly displayName = computed(() => this.getDisplayName(this.user()));
  readonly formDisabled = computed(() => this.initialLoading() || this.isAnythingLoading());

  isAnythingLoading = computed<boolean>(() => {
       const requestInFlight = this.authService.loadingObjectSignal()['user'] === true;
       return requestInFlight || this.loadingService.isAnythingLoading(this.loadingObject());
   });
  setLoadingStatus = (key: string, value: boolean): void => {
    this.loadingObject.update((current) => this.loadingService.setLoadingStatus(key, value, current));
  };


  constructor() {
    effect(() => {
      const currentUser = this.user();

      if (this.formDisabled()) {
        this.nameForm.disable({ emitEvent: false });
      } else {
        this.nameForm.enable({ emitEvent: false });
      }

      if (currentUser && !this.nameForm.dirty) {
        this.patchFormFromUser(currentUser);
      }
    });
  }

  ngOnInit(): void {
    this.authService.user
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
      this.user.set(user);
      });
  }

  private getDisplayName(user: User | null | undefined): string {
    if (!user) {
      return '';
    }

    return [user.pre_honorific, user.fname, user.lname, user.post_honorific]
      .filter((value) => Boolean(value && value.trim()))
      .join(' ');
  }

  private patchFormFromUser(user: User): void {
    this.nameForm.reset(
      {
        fname: user.fname ?? '',
        lname: user.lname ?? '',
        pre_honorific: user.pre_honorific ?? '',
        post_honorific: user.post_honorific ?? '',
      },
      { emitEvent: false },
    );
    this.nameForm.markAsPristine();
    this.nameForm.markAsUntouched();
  }

  private applyFieldErrors(errorResponse: any): boolean {
    const validationErrors = errorResponse?.errors;

    if (!validationErrors) {
      return false;
    }

    const entries = Object.entries(validationErrors);
    if (!entries.length) {
      return false;
    }

    entries.forEach(([fieldName, messages]) => {
      const control = this.nameForm.get(fieldName);
      if (!control) {
        return;
      }

      const messageList = Array.isArray(messages) ? messages : [messages];
      const backendMessage = String(messageList[0] ?? '').toLowerCase();
      const nextErrors: Record<string, boolean> = { ...(control.errors ?? {}) };

      if (backendMessage.includes('at least')) {
        nextErrors.minlength = true;
      } else if (backendMessage.includes('greater than')) {
        nextErrors.maxlength = true;
      } else if (backendMessage.includes('required')) {
        nextErrors.required = true;
      } else {
        nextErrors.backend = true;
      }

      control.setErrors(nextErrors);
      control.markAsTouched();
    });

    return true;
  }

  private buildRequestBody(): UpdateUserRequestBody {
    return {
      fname: this.nameForm.controls.fname.value.trim(),
      lname: this.nameForm.controls.lname.value.trim(),
      pre_honorific: this.nameForm.controls.pre_honorific.value.trim(),
      post_honorific: this.nameForm.controls.post_honorific.value.trim(),
    };
  }

  saveName(): void {
    this.submitAttempted.set(true);

    if (this.isAnythingLoading()) {
      return;
    }

    if (this.nameForm.invalid) {
      this.nameForm.markAllAsTouched();
      return;
    }

    this.error.set(null);
    this.setLoadingStatus('user', true);

    this.authService
      .updateUser(this.buildRequestBody())
      .pipe(
        finalize(() => {
          this.setLoadingStatus('user', false);
        }),
      )
      .subscribe({
        next: () => {
          const latestUser = this.user();
          if (latestUser) {
            this.patchFormFromUser(latestUser);
          }
          this.submitAttempted.set(false);
        },
        error: (errorResponse) => {
          const matchedFieldErrors = this.applyFieldErrors(errorResponse);
          if (!matchedFieldErrors) {
            this.error.set(
              typeof errorResponse === 'string'
                ? errorResponse
                : errorResponse?.message ?? 'multisite.something_went_wrong',
            );
          }
        },
      });
  }

}
